import { useEffect, useRef, useState } from 'react';
import { backend } from '@/api/backend';

/**
 * The whole graph, not its first page.
 *
 * The projection route returns `nextCursor`, `nodesTruncated` and
 * `edgesTruncated`, and the graph page read none of them: it drew the first
 * page and presented it as the graph. There was no marker, no count, and no
 * way to tell a small corpus from a truncated view of a large one. Silently
 * dropping the rest of a customer's data is the one thing this product must
 * never do.
 *
 * So the cursor is followed to exhaustion. Pages are bounded, because a canvas
 * cannot draw an unbounded graph and pretending otherwise would trade a silent
 * omission for a frozen tab - but the bound is stated, with the count reached,
 * rather than hidden.
 */

/** Pages followed before stopping and saying so. */
export const MAX_PAGES = 40;

export interface GraphPage {
  nodes: unknown[];
  edges: unknown[];
  nextCursor?: string | null;
  nodesTruncated?: boolean;
  edgesTruncated?: boolean;
}

export interface WholeGraph {
  nodes: unknown[];
  edges: unknown[];
  /** Pages fetched beyond the first. */
  pages: number;
  /** Still following the cursor. */
  loading: boolean;
  /** The backend still had more when the page bound was reached. */
  stoppedEarly: boolean;
  /** The backend itself truncated a page. */
  serverTruncated: boolean;
}

function page(value: unknown): GraphPage | null {
  if (!value || typeof value !== 'object') return null;
  const v = (value as { data?: unknown }).data ?? value;
  if (!v || typeof v !== 'object') return null;
  const r = v as Record<string, unknown>;
  if (!Array.isArray(r.nodes)) return null;
  return {
    nodes: r.nodes as unknown[],
    edges: Array.isArray(r.edges) ? (r.edges as unknown[]) : [],
    nextCursor: typeof r.nextCursor === 'string' && r.nextCursor ? r.nextCursor : null,
    nodesTruncated: r.nodesTruncated === true,
    edgesTruncated: r.edgesTruncated === true,
  };
}

/**
 * Follows `first`'s cursor across every remaining page.
 *
 * Returns only the pages beyond the first: the caller already has page one and
 * merges these into it, so a re-render never re-reads what it is drawing.
 */
/**
 * Follows every listed bucket's cursor across all remaining pages.
 *
 * One hook over the whole list, never one per bucket: the expanded set changes
 * as an operator opens and closes scopes, and a hook called inside a map would
 * break the moment it did.
 *
 * Returns only the pages beyond the first, keyed by position, because the
 * caller already holds page one and merges these into it.
 */
export function useWholeGraphs(bucketIds: string[], firsts: unknown[]): WholeGraph[] {
  const [byBucket, setByBucket] = useState<Record<string, WholeGraph>>({});
  const walked = useRef<Record<string, string>>({});

  const cursors = bucketIds.map((id, i) => `${id}:${page(firsts[i])?.nextCursor ?? ''}`).join('|');

  useEffect(() => {
    let cancelled = false;

    for (const [i, bucketId] of bucketIds.entries()) {
      const head = page(firsts[i]);
      if (!head) continue;
      const key = `${bucketId}:${head.nextCursor ?? ''}`;
      if (walked.current[bucketId] === key) continue;
      walked.current[bucketId] = key;

      const serverTruncated = head.nodesTruncated === true || head.edgesTruncated === true;
      if (!head.nextCursor) {
        setByBucket((s) => ({
          ...s,
          [bucketId]: { nodes: [], edges: [], pages: 0, loading: false, stoppedEarly: false, serverTruncated },
        }));
        continue;
      }

      void (async () => {
        const nodes: unknown[] = [];
        const edges: unknown[] = [];
        let truncated = serverTruncated;
        let cursor: string | null = head.nextCursor ?? null;
        let count = 0;
        const publish = (loading: boolean) => {
          if (cancelled) return;
          setByBucket((s) => ({
            ...s,
            [bucketId]: {
              nodes: [...nodes],
              edges: [...edges],
              pages: count,
              loading,
              stoppedEarly: Boolean(cursor) && count >= MAX_PAGES,
              serverTruncated: truncated,
            },
          }));
        };
        publish(true);
        while (cursor && count < MAX_PAGES && !cancelled) {
          let next: GraphPage | null = null;
          try {
            next = page(await backend.expandGraphProjection(bucketId, cursor));
          } catch {
            // A page that will not load stops this bucket's walk. What was
            // collected still draws, and the count reached is reported.
            break;
          }
          if (!next) break;
          nodes.push(...next.nodes);
          edges.push(...next.edges);
          truncated = truncated || next.nodesTruncated === true || next.edgesTruncated === true;
          count += 1;
          cursor = next.nextCursor ?? null;
          publish(Boolean(cursor) && count < MAX_PAGES);
        }
        publish(false);
      })();
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursors]);

  const empty: WholeGraph = {
    nodes: [],
    edges: [],
    pages: 0,
    loading: false,
    stoppedEarly: false,
    serverTruncated: false,
  };
  return bucketIds.map((id) => byBucket[id] ?? empty);
}
